import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import {
  faBan,
  faCheck,
  faEye,
  faPen,
  faPlus,
  faPlusCircle,
  faTimes,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import { NbToastrService } from "@nebular/theme";
import {
  ColumnModel,
  FilterSettingsModel,
  GridComponent,
  PageSettingsModel,
} from "@syncfusion/ej2-angular-grids";
import { forkJoin } from "rxjs";
import { ErrorService } from "../../../../@core/utils/error.service";
import { KmlService } from "../../../../@core/utils/kml.service";
import { SessionService } from "../../../../@core/utils/session.service";
import {
  ConfirmationData,
  ConfirmationDialogComponent,
} from "../../../../partials/content/widgets/general/confirmation-dialog/confirmation-dialog.component";
import { TruckType } from "../../../modules/pick-plan/models/truck-type";
import { truckTypeService } from "../../../modules/pick-plan/services/truck-type.service";
import { KwFilterDropdownService } from "../../widgets/kw-filter-dropdown/kw-filter-dropdown.service";
import { TruckTypeDetailsComponent } from "./truck-type-details/truck-type-details.component";


@Component({
  selector: 'truck-type-list',
  templateUrl: './truck-type-list.component.html',
  styleUrls: ['./truck-type-list.component.scss']
})
export class TruckTypeListComponent implements OnInit {
private destroyRef = inject(DestroyRef);
//#region Properties
public editActive = false;
public truckTypeId: number;
public truckTypeLoading = true;
public submitting: any;
public editTruckType: TruckType = {};
public faTimes = faTimes;
public faCheck = faCheck;
public allTruckTypes: TruckType[] = [];
public savingTruckTypeIds: Set<number> = new Set<number>();
public highlightedTruckTypeId: number = null;
private pendingScrollToTruckTypeId: number = null;
public allColumns: ColumnModel[] = [];
public gridName: string = "TruckTypeConfigGrid";
public pageSettings: PageSettingsModel = {
  pageSize: 50,
  pageSizes: [50, 100, 200],
};
public filterSettings: FilterSettingsModel = {
  mode: "Immediate",
  type: "Excel",
};
public toolbarOptions: string[] = [
  "ExcelExport",
];
public enablePersistence: boolean = true;
public filterCount: number = 0;
//#endregion

//#region Icon Properties
public faPlus = faPlus;
public faPen = faPen;
public faEye = faEye;
public faTrash = faTrash;
public faPlusCircle = faPlusCircle;
public faBan = faBan;
//#endregion

//#region ViewChild Properties
@ViewChild("grid", { static: true }) grid: GridComponent;

@ViewChild("actionTemplate", { static: true })
public actionTemplate: any;

@ViewChild("isActiveTemplate", { static: true })
public isActiveTemplate: any;
//#endregion
 
  //#region Constructor with dependecies
  constructor(
    private errorService: ErrorService,
    public truckTypeService: truckTypeService,
    private kmlService: KmlService,
    private toastrService: NbToastrService,
    private sessionService: SessionService,
    public dialogService: MatDialog,
    private filterService: KwFilterDropdownService
  ) {}
  //#endregion

  ngOnInit() {
    this.loadData();
  }
  loadData() {
    this.truckTypeLoading = true;

    forkJoin([
      this.truckTypeService.getTruckTypes(true),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:
      (results) => {
        this.allTruckTypes = results[0];

        this.truckTypeLoading = false;
        this.setColumns();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.truckTypeLoading = false;
      }}
    );
  }

  setColumns() {
    this.allColumns = [];

    // Hidden primary key column - lets us look up/select a specific row
    // (e.g. to scroll to and highlight a newly created truck type).
    this.allColumns.push({
      field: "id",
      isPrimaryKey: true,
      visible: false,
    });

    this.allColumns.push(
      ...[
        {
          field: "name",
          headerText: "Truck Type Name",
        },
        {
          field: "description",
          headerText: "Description",
        },
        {
          headerText: "Action",
          template: this.actionTemplate,
          width: 200,
        },
      ]
    );
  }

  onActionComplete(args: any) {
    if (args.requestType === 'filtering') {
      this.updateFilterCount();
    }
  }

  onGridDataBound() {
    this.updateFilterCount();

    if (this.pendingScrollToTruckTypeId != null) {
      // goToPage() can fire this from a render that still has stale data
      // (queued just ahead of the refresh that actually adds the row), so
      // only clear the pending id once we've actually found and scrolled to
      // it - otherwise keep waiting for the next dataBound to try again.
      const id = this.pendingScrollToTruckTypeId;
      const rowIndex = this.grid.getRowIndexByPrimaryKey(id);
      if (rowIndex > -1) {
        this.scrollGridRowIntoView(this.grid.getRowByIndex(rowIndex));
        this.pendingScrollToTruckTypeId = null;
        this.startHighlightFade(this.grid.getRowByIndex(rowIndex), id);

        // Something can quietly reset the content scroll a moment after
        // this - reassert it once more shortly after to counteract that.
        setTimeout(() => {
          const idx = this.grid.getRowIndexByPrimaryKey(id);
          if (idx > -1) {
            const row = this.grid.getRowByIndex(idx);
            this.scrollGridRowIntoView(row);
            this.startHighlightFade(row, id);
          }
        }, 200);
      }
    }
  }

  private updateFilterCount() {
    const cols = (this.grid && this.grid.filterSettings && this.grid.filterSettings.columns) ? this.grid.filterSettings.columns : [];
    const uniqueFields = cols.map(c => c.field as string).filter((f: string, i: number, a: string[]) => a.indexOf(f) === i);
    this.filterCount = uniqueFields.length;
  }

  clearFilters() {
    window.localStorage.setItem("gridBlocksConfigFilters", "");
    this.grid.clearFiltering();
    this.filterCount = 0;
  }

  toolbarClick(args) {
    this.truckTypeLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.item.properties.text == "Excel Export") {
      this.grid
        .excelExport({
          fileName: `TruckTypeExport.xlsx`,
        })
        .then(() => {
          this.truckTypeLoading = false;
        });
    }
  }

  confirmDelete(id: number) {
    const confirmDialogData: ConfirmationData = {
      title: "Confirm Deletion",
      confirmMessage: "Are you sure you want to delete this truck type?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      displayComment: false,
      isWarning: false,
    };

    const confirmDialog = this.dialogService.open(ConfirmationDialogComponent, {
      width: "400px",
      data: confirmDialogData,
    });

    confirmDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result === true) {
        this.deleteRec(id);
      }
    });
  }

  deleteRec(id: number) {
    this.truckTypeLoading = true;
    this.truckTypeService.deleteTruckTypeDTO(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      {next:() => {
        // Keep the filter dropdown cache in sync so other pages drop the deleted truck type immediately
        this.filterService.removeCached('truckType', id);
        this.toastrService.info("Truck type successfully deleted!");
        this.truckTypeLoading = false;
        this.loadData();
      },
      error:(err) => {
        this.errorService.handleError(err);
        this.truckTypeLoading = false;
      }}
    );
  }

  
  openDialog(IsEdit: boolean, data?: TruckType) {
    const truckTypeDetailsDialog = this.dialogService.open(TruckTypeDetailsComponent, {
      width: "500px",
    });
    if (IsEdit) {
      var persistData = (this.grid as GridComponent).getPersistData();
      window.localStorage.setItem("grid" + this.gridName, persistData);
      this.sessionService.selectedTruckType = { ...data };
    } else {
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedTruckType = {} as TruckType;
    }

    truckTypeDetailsDialog.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result && result.backgroundSave && result.truckType) {
        if (result.isNew) {
          this.createTruckTypeInBackground(result.truckType);
        } else {
          this.saveTruckTypeInBackground(result.truckType);
        }
      } else if (result) {
        this.loadData();
      }
      window.localStorage.removeItem("grid" + this.gridName);
      this.sessionService.selectedTruckType = {} as TruckType;
    });
  }

  private createTruckTypeInBackground(truckType: TruckType): void {
    // The truck type doesn't exist server-side yet, so show a temporary
    // placeholder row (negative id so it can't collide with a real one)
    // with the saving spinner until the server assigns a real id.
    const tempId = -Date.now();
    this.insertOrReplaceTruckTypeRow({ ...truckType, id: tempId });
    this.savingTruckTypeIds.add(tempId);
    this.refreshGridSoon();

    this.truckTypeService.postTruckTypeDTO(truckType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (created) => {
        this.removeTruckTypeRow(tempId);
        this.savingTruckTypeIds.delete(tempId);
        this.filterService.upsertCached('truckType', created);
        this.insertOrReplaceTruckTypeRow(created);
        this.refreshGridSoon();
        this.jumpToTruckTypeRow(created.id);
        this.toastrService.success(
          "Truck Type Details successfully saved!",
          (created.name || truckType.name) + " saved!"
        );
      },
      error: (err) => {
        this.removeTruckTypeRow(tempId);
        this.savingTruckTypeIds.delete(tempId);
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to create Truck Type "' + (truckType.name || "truck type") + '". Please try again.',
          "Create failed"
        );
      }
    });
  }

  private saveTruckTypeInBackground(truckType: TruckType): void {
    const originalTruckType = this.allTruckTypes.find((t) => t.id === truckType.id);
    this.savingTruckTypeIds.add(truckType.id);
    this.insertOrReplaceTruckTypeRow(truckType);
    this.refreshGridSoon();

    this.truckTypeService.putTruckTypeDTO(truckType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (updated) => {
        this.savingTruckTypeIds.delete(truckType.id);
        this.filterService.upsertCached('truckType', updated || truckType);
        this.insertOrReplaceTruckTypeRow(updated || truckType);
        this.refreshGridSoon();
        this.toastrService.success(
          "Truck Type Details successfully updated!",
          truckType.name + " updated!"
        );
      },
      error: (err) => {
        this.savingTruckTypeIds.delete(truckType.id);
        if (originalTruckType) {
          this.insertOrReplaceTruckTypeRow(originalTruckType);
        }
        this.refreshGridSoon();
        this.errorService.handleError(err);
        this.toastrService.danger(
          'Failed to update Truck Type "' + truckType.name + '". Please try again.',
          "Save failed"
        );
      }
    });
  }

  private insertOrReplaceTruckTypeRow(truckType: TruckType): void {
    const index = this.allTruckTypes.findIndex((t) => t.id === truckType.id);
    const next = this.allTruckTypes.slice();
    if (index !== -1) {
      next[index] = truckType;
    } else {
      next.push(truckType);
    }
    this.allTruckTypes = next;
  }

  private removeTruckTypeRow(id: number): void {
    this.allTruckTypes = this.allTruckTypes.filter((t) => t.id !== id);
    this.refreshGridSoon();
  }

  // Defers the grid refresh to the next macrotask so Angular has already
  // pushed the updated allTruckTypes/savingTruckTypeIds state into the grid's
  // own [dataSource] binding before we force it to re-render.
  private refreshGridSoon(): void {
    setTimeout(() => {
      if (this.grid) {
        this.grid.refresh();
      }
    });
  }

  private isDefaultSortActive(): boolean {
    const cols = (this.grid && this.grid.sortSettings && this.grid.sortSettings.columns) || [];
    return cols.length === 0;
  }

  private jumpToTruckTypeRow(id: number): void {
    this.highlightedTruckTypeId = id;
    // Scrolling happens once the grid actually finishes its next render
    // (see onGridDataBound) rather than after a guessed delay.
    this.pendingScrollToTruckTypeId = id;

    if (!this.grid || !this.isDefaultSortActive()) {
      // Can't reliably compute which page the row lands on under a custom
      // sort - it'll still get highlighted/scrolled to whenever it's next
      // rendered on whatever page the user navigates to.
      return;
    }

    // Defer past the refresh(es) already queued by insertOrReplaceTruckTypeRow/
    // removeTruckTypeRow so this pages using the grid's up-to-date data.
    setTimeout(() => {
      const index = this.allTruckTypes.findIndex((t) => t.id === id);
      if (index === -1) {
        return;
      }

      const pageSize = this.pageSettings.pageSize || this.allTruckTypes.length;
      const page = Math.floor(index / pageSize) + 1;

      if (this.grid.pageSettings.currentPage !== page) {
        this.grid.goToPage(page);
      }
    });
  }

  // Scrolls the grid's own internal content viewport, not the page - a
  // plain row.scrollIntoView() walks every scrollable ancestor (including
  // the page).
  private scrollGridRowIntoView(row: Element): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    const scrollableContent = this.grid.getContent().querySelector(".e-content") as HTMLElement;
    if (scrollableContent) {
      const targetScrollTop = rowElement.offsetTop
        - (scrollableContent.clientHeight / 2)
        + (rowElement.offsetHeight / 2);
      scrollableContent.scrollTo({ top: Math.max(targetScrollTop, 0), behavior: "auto" });
    } else if (typeof rowElement.scrollIntoView === "function") {
      rowElement.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }

  private startHighlightFade(row: Element, id: number): void {
    if (!row) {
      return;
    }

    const rowElement = row as HTMLElement;
    rowElement.classList.add("row-highlight-new");
    if (this.highlightedTruckTypeId === id) {
      this.highlightedTruckTypeId = null;
    }
    setTimeout(() => {
      rowElement.classList.remove("row-highlight-new");
    }, 2000);
  }

  onRowDataBound(args: any): void {
    if (args.data && this.savingTruckTypeIds.has(args.data.id)) {
      args.row.classList.add('row-saving');
    } else if (args.row) {
      args.row.classList.remove('row-saving');
    }
    if (args.data && this.highlightedTruckTypeId != null && args.data.id === this.highlightedTruckTypeId) {
      args.row.classList.add('row-highlight-new');
    }
  }

  downloadFiles(args:string) {
    this.truckTypeLoading = true;
    this.grid.aggregates = [];
    this.grid.refresh();

    if (args.includes("Excel")) {
      this.grid
        .excelExport({
          fileName: `TruckTypeExport.xlsx`,
        })
        .then(() => {
          this.truckTypeLoading = false;
        });
    }
  }
}
